import { useEffect } from 'react';
import { useForm, type DefaultValues } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { Gift, Tags } from 'lucide-react';
import { addMonths } from 'date-fns';
import {
  FormDateField,
  FormDialog,
  FormSection,
  FormSelectField,
  FormStatusField,
  FormTextareaField,
  FormTextField,
} from '@/components/common/FormDialog';
import { bookEditionsQueryOptions } from '@/features/product/api/product.api';
import { formatDate, toDateFieldValue } from '@/utils/date';
import { promoItemTypesQueryOptions } from '../api/promotion.api';
import { ALL_ROWS_LOOKUP_PARAMS } from '../constants/promotion.constants';
import { useSavePromoItem, useSavePromoItemType } from '../hooks/usePromoMutations';
import {
  promoItemFormSchema,
  promoItemTypeFormSchema,
  type PromoItem,
  type PromoItemFormValues,
  type PromoItemType,
  type PromoItemTypeFormValues,
} from '../types/promotion.types';

const DEFAULT_ACTIVE_MONTHS = 3;

const TYPE_DEFAULTS: PromoItemTypeFormValues = {
  name: '',
  code: '',
  description: '',
  slNum: '',
  isActive: false,
  activeFrom: '',
  activeTo: '',
};

const itemDefaults = (): DefaultValues<PromoItemFormValues> => ({
  name: '',
  code: '',
  description: '',
  slNum: '',
  isActive: false,
  activeFrom: formatDate(new Date()),
  activeTo: formatDate(addMonths(new Date(), DEFAULT_ACTIVE_MONTHS)),
  isView: false,
  isTabView: false,
});

const numberOrUndefined = (value: string): number | undefined => {
  if (value.trim() === '') return undefined;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

interface TypeModalProps {
  itemType: PromoItemType | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PromoItemTypeFormModal({ itemType, open, onOpenChange }: TypeModalProps) {
  const form = useForm<PromoItemTypeFormValues>({
    resolver: zodResolver(promoItemTypeFormSchema),
    defaultValues: TYPE_DEFAULTS,
  });

  useEffect(() => {
    form.reset(
      itemType === null
        ? TYPE_DEFAULTS
        : {
            name: itemType.name,
            code: itemType.code ?? '',
            description: itemType.description ?? '',
            slNum: itemType.slNum === null ? '' : String(itemType.slNum),
            isActive: itemType.isActive,
            activeFrom: toDateFieldValue(itemType.activeFrom),
            activeTo: toDateFieldValue(itemType.activeTo),
          },
    );
  }, [itemType, open, form]);

  const save = useSavePromoItemType(() => onOpenChange(false));

  function submit(values: PromoItemTypeFormValues) {
    const slNum = numberOrUndefined(values.slNum);
    save.mutate({
      id: itemType?.id ?? null,
      payload: {
        name: values.name,
        code: values.code,
        description: values.description.trim(),
        isActive: values.isActive,
        ...(slNum === undefined ? {} : { slNum }),
        ...(values.activeFrom === '' ? {} : { activeFrom: values.activeFrom }),
        ...(values.activeTo === '' ? {} : { activeTo: values.activeTo }),
      },
    });
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      icon={Tags}
      title={itemType === null ? 'Add Item Type' : 'Edit Item Type'}
      description="A type groups the promotional items the field hands out."
      form={form}
      isPending={save.isPending}
      isEditMode={itemType !== null}
      onSubmit={submit}
    >
      <FormSection>
        <FormTextField
          control={form.control}
          name="name"
          label="Type Name"
          placeholder="Umbrella"
          isRequired
          isFullRow
        />
        <FormTextField
          control={form.control}
          name="code"
          label="Type Code"
          placeholder="U0001"
          hasTabularNumbers
          isRequired
        />
        <FormTextField
          control={form.control}
          name="slNum"
          label="Sl Num"
          placeholder="Optional"
          inputMode="numeric"
          hasTabularNumbers
        />
        <FormTextareaField
          control={form.control}
          name="description"
          label="Description"
          isRequired
          isFullRow
        />
        <FormDateField control={form.control} name="activeFrom" label="Active From" />
        <FormDateField control={form.control} name="activeTo" label="Active To" />
      </FormSection>
      <FormStatusField control={form.control} name="isActive" />
    </FormDialog>
  );
}

interface ItemModalProps {
  item: PromoItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PromoItemFormModal({ item, open, onOpenChange }: ItemModalProps) {
  const form = useForm<PromoItemFormValues>({
    resolver: zodResolver(promoItemFormSchema),
    defaultValues: itemDefaults(),
  });

  const { data: types, isLoading: typesLoading } = useQuery(
    promoItemTypesQueryOptions(ALL_ROWS_LOOKUP_PARAMS),
  );
  const { data: editions, isLoading: editionsLoading } = useQuery(
    bookEditionsQueryOptions(ALL_ROWS_LOOKUP_PARAMS),
  );

  useEffect(() => {
    form.reset(
      item === null
        ? itemDefaults()
        : {
            name: item.name,
            code: item.code ?? '',
            description: item.description ?? '',
            slNum: item.slNum === null ? '' : String(item.slNum),
            isActive: item.isActive,
            activeFrom: toDateFieldValue(item.activeFrom),
            activeTo: toDateFieldValue(item.activeTo),
            isView: item.isView,
            isTabView: item.isTabView,
            ...(item.itemTypeId === null ? {} : { itemTypeId: item.itemTypeId }),
            ...(item.bookEditionNo === null ? {} : { bookEditionNo: item.bookEditionNo }),
          },
    );
  }, [item, open, form]);

  const save = useSavePromoItem(() => onOpenChange(false));

  const typeOptions = (types?.data ?? []).map((type) => ({ id: type.id, name: type.name }));

  const savedEditionNo = item?.bookEditionNo ?? null;
  const editionOptions = (editions?.data ?? []).flatMap((edition) => {
    if (edition.legacyId === null) return [];
    const isOffered = edition.isActive || edition.legacyId === savedEditionNo;
    return isOffered ? [{ id: edition.legacyId, name: edition.name }] : [];
  });

  function submit(values: PromoItemFormValues) {
    const slNum = numberOrUndefined(values.slNum);
    save.mutate({
      id: item?.id ?? null,
      payload: {
        name: values.name,
        code: values.code,
        description: values.description.trim(),
        itemTypeId: values.itemTypeId,
        bookEditionNo: values.bookEditionNo,
        isActive: values.isActive,
        isView: values.isView,
        isTabView: values.isTabView,
        ...(slNum === undefined ? {} : { slNum }),
        ...(values.activeFrom === '' ? {} : { activeFrom: values.activeFrom }),
        ...(values.activeTo === '' ? {} : { activeTo: values.activeTo }),
      },
    });
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      icon={Gift}
      title={item === null ? 'Add Promotional Item' : 'Edit Promotional Item'}
      description="The item the field hands over on a DCR."
      form={form}
      isPending={save.isPending}
      isEditMode={item !== null}
      onSubmit={submit}
      width="wide"
    >
      <FormSection>
        <FormTextField
          control={form.control}
          name="name"
          label="Promotion Name"
          placeholder="Desk Calendar-2026"
          isRequired
          isFullRow
        />
        <FormTextField
          control={form.control}
          name="code"
          label="Item Code"
          placeholder="GCC25-26"
          hasTabularNumbers
          isRequired
        />
        <FormTextField
          control={form.control}
          name="slNum"
          label="Sl Num"
          placeholder="Optional"
          inputMode="numeric"
          hasTabularNumbers
        />
        <FormSelectField
          control={form.control}
          name="itemTypeId"
          label="Type"
          options={typeOptions}
          isLoading={typesLoading}
          placeholder="Pick a type"
          isRequired
        />
        <FormSelectField
          control={form.control}
          name="bookEditionNo"
          label="Book Edition"
          options={editionOptions}
          isLoading={editionsLoading}
          placeholder="Pick an edition"
          isRequired
        />
        <FormTextareaField
          control={form.control}
          name="description"
          label="Item Description"
          isRequired
          isFullRow
        />
        <FormDateField control={form.control} name="activeFrom" label="Active From" />
        <FormDateField control={form.control} name="activeTo" label="Active To" />
      </FormSection>
      <div className="grid gap-3 sm:grid-cols-3">
        <FormStatusField control={form.control} name="isActive" />
        <FormStatusField
          control={form.control}
          name="isView"
          label="Is View"
          hint="Lists the item on Market Promotion Approval and its project filters."
        />
        <FormStatusField
          control={form.control}
          name="isTabView"
          label="Is Tab View"
          hint="Gives the item its own tab on the promotion dashboards."
        />
      </div>
    </FormDialog>
  );
}
